import * as i0 from '@angular/core';
import { Pipe, NgModule, EventEmitter, Directive, Inject, Host, Output, Input, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, fromEvent, takeUntil, throttle, timer } from 'rxjs';
import { WINDOW } from '@mercadona/core/utils/tokens';
import * as i1 from '@mercadona/components/tooltip';
import { coerceNumberProperty } from '@angular/cdk/coercion';
import { filter, tap } from 'rxjs/operators';

class MArrayGeneratorPipe {
    transform(value) {
        const result = [];
        for (let i = 0; i < value; i++) {
            result.push(i);
        }
        return result;
    }
}
MArrayGeneratorPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MArrayGeneratorPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
MArrayGeneratorPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.2.9", ngImport: i0, type: MArrayGeneratorPipe, isStandalone: true, name: "mArrayGenerator" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MArrayGeneratorPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'mArrayGenerator', standalone: true }]
        }] });

class MHighlightSearchPipe {
    transform(originalText, searchValues, caseSensitive = false, cssClass = 'm-highlight-search') {
        if (typeof originalText !== 'string' || !searchValues) {
            return originalText;
        }
        if (caseSensitive) {
            return this._transformWithCaseSensitive(originalText, searchValues, cssClass);
        }
        return this._transformWithoutCaseSensitive(originalText, searchValues, cssClass);
    }
    /**
     * Transform function when caseSensitive is false
     *
     * @param {string} originalText original text where to search
     * @param {string} searchValues values to search
     * @param {string} cssClass css class, used to modify styles of results of search
     * @returns {string} original text or modified text with search results
     */
    _transformWithoutCaseSensitive(originalText, searchValues, cssClass) {
        const foundWords = new Map();
        const normalizedText = originalText
            .toLowerCase()
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '');
        let wordIndex = normalizedText.length - 1;
        const searchNormalized = searchValues
            .toLowerCase()
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '');
        while (wordIndex > 0) {
            wordIndex = normalizedText.lastIndexOf(searchNormalized, wordIndex - 1);
            const foundWord = originalText.substring(wordIndex, wordIndex + searchValues.length);
            if (wordIndex > -1) {
                foundWords.set(wordIndex, foundWord);
            }
        }
        let output = originalText;
        const foundWordsEntries = [...foundWords.entries()];
        for (let i = foundWordsEntries.length - 1; i >= 0; i--) {
            const foundItem = foundWordsEntries[i];
            const prevFoundItem = foundWordsEntries[i - 1];
            if (foundItem && prevFoundItem && foundItem[0] + foundItem[1].length >= prevFoundItem[0]) {
                foundWords.delete(prevFoundItem[0]);
                i--;
            }
        }
        foundWords.forEach((foundWord, foundIndex) => {
            output =
                (foundIndex > 0 ? output.substring(0, foundIndex) : '') +
                    `<span class=${cssClass}>${foundWord}</span>` +
                    output.substring(foundIndex + searchValues.length);
        });
        return output;
    }
    /**
     * Transform function when caseSensitive is true
     *
     * @param {string} originalText original text where to search
     * @param {string} searchValues values to search
     * @param {string} cssClass css class, used to modify styles of results of search
     * @returns {string} original text or modified text with search results
     */
    _transformWithCaseSensitive(originalText, searchValues, cssClass) {
        const regex = new RegExp(searchValues, 'g');
        return originalText.replace(regex, (match) => `<span class=${cssClass}>${match}</span>`);
    }
}
MHighlightSearchPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MHighlightSearchPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
MHighlightSearchPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.2.9", ngImport: i0, type: MHighlightSearchPipe, isStandalone: true, name: "mHighlightSearch" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MHighlightSearchPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'mHighlightSearch',
                    standalone: true
                }]
        }] });

/**
 * @deprecated Warning: Deprecated module. We prescribe the use of MArrayGeneratorPipe. It will be removed in 3.0.0.
 */
class MCommonPipesModule {
}
MCommonPipesModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MCommonPipesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MCommonPipesModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.9", ngImport: i0, type: MCommonPipesModule, imports: [CommonModule, MArrayGeneratorPipe], exports: [MArrayGeneratorPipe] });
MCommonPipesModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MCommonPipesModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MCommonPipesModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, MArrayGeneratorPipe],
                    exports: [MArrayGeneratorPipe]
                }]
        }] });

// Use an unicode character to delimit the words in the concatenated string.
// This one is "Large circle" from https://en.wikipedia.org/wiki/List_of_Unicode_characters
const stringSeparator = '◯';

const calculateTextWidth = (text, element, document, window) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (context) {
        const styles = window.getComputedStyle(element);
        context.font = `${styles.fontWeight} ${styles.fontSize} ${styles.fontFamily}`;
        return context.measureText(text).width;
    }
    return 0;
};

const isValidOpenKeyCode = (event) => {
    const keyCode = event.key || event.code;
    return keyCode === 'Enter' || keyCode === 'Space' || keyCode === ' ';
};

class MDisplayTooltipEllipsisDirective {
    constructor(_window, mTooltipDirective, elementRef, ngZone, _changeDetectorRef) {
        this._window = _window;
        this.mTooltipDirective = mTooltipDirective;
        this.elementRef = elementRef;
        this.ngZone = ngZone;
        this._changeDetectorRef = _changeDetectorRef;
        this._destroy$ = new Subject();
        /**
         * Output to emmit the string value of the element that mouse enter or leave
         */
        this.mouseEventEmitter = new EventEmitter();
    }
    ngOnInit() {
        this.ngZone.runOutsideAngular(() => {
            fromEvent(this.elementRef.nativeElement, 'mouseenter')
                .pipe(takeUntil(this._destroy$))
                .subscribe(() => {
                this.mouseEventEmitter.emit(this.elementRef.nativeElement.textContent);
            });
            fromEvent(this.elementRef.nativeElement, 'mouseleave')
                .pipe(takeUntil(this._destroy$))
                .subscribe(() => {
                this.mouseEventEmitter.emit(this.elementRef.nativeElement.textContent);
            });
        });
    }
    ngAfterViewInit() {
        this.ngZone.runOutsideAngular(() => {
            if (typeof this._window !== 'undefined' && ResizeObserver) {
                this._resizeObserverConstructor = new ResizeObserver((entries) => {
                    /**
                     * Wrap it in requestAnimationFrame to avoid this error - ResizeObserver loop limit exceeded
                     * The window.requestAnimationFrame() method tells the browser that you wish to perform an animation
                     * and requests that the browser calls a specified function to update an animation before the next repaint.
                     */
                    this._window.requestAnimationFrame(() => {
                        if (!Array.isArray(entries) || !entries.length) {
                            return;
                        }
                        this.ngZone.run(() => {
                            this._refresh();
                        });
                    });
                });
                this._resizeObserverConstructor.observe(this.elementRef.nativeElement);
            }
        });
    }
    ngOnChanges(changes) {
        const { valueToShow } = changes;
        if (valueToShow) {
            this._refresh();
        }
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
        this._resizeObserverConstructor?.disconnect();
    }
    _resize(entries) {
        if (!Array.isArray(entries) || !entries.length) {
            return;
        }
        if (entries[0].contentRect.width) {
            this.ngZone.run(() => this._refresh());
        }
    }
    _refresh() {
        const inputElement = this.elementRef.nativeElement;
        if (inputElement) {
            // Need change detection for the clear
            this._changeDetectorRef.detectChanges();
            const scrollWidth = inputElement.scrollWidth;
            const clientWidth = inputElement.clientWidth;
            this.mTooltipDirective.disabled = scrollWidth <= clientWidth;
        }
    }
}
MDisplayTooltipEllipsisDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MDisplayTooltipEllipsisDirective, deps: [{ token: WINDOW }, { token: i1.MTooltipDirective, host: true }, { token: i0.ElementRef }, { token: i0.NgZone }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive });
MDisplayTooltipEllipsisDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.9", type: MDisplayTooltipEllipsisDirective, isStandalone: true, selector: "[m-display-tooltip-ellipsis]", outputs: { mouseEventEmitter: "m-display-tooltip-ellipsis" }, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MDisplayTooltipEllipsisDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[m-display-tooltip-ellipsis]',
                    standalone: true
                }]
        }], ctorParameters: function () { return [{ type: Window, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: i1.MTooltipDirective, decorators: [{
                    type: Host
                }] }, { type: i0.ElementRef }, { type: i0.NgZone }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { mouseEventEmitter: [{
                type: Output,
                args: ['m-display-tooltip-ellipsis']
            }] } });

const defaultDebounceTime = 0;
class MClickDirective {
    constructor() {
        this._destroy$ = new Subject();
        this._internalClick$ = new Subject();
        this._isActionEnabled = true;
        this._clickDebounceTime = defaultDebounceTime;
        this.clickPreventChange = new EventEmitter();
    }
    get clickDebounceTime() {
        return this._clickDebounceTime;
    }
    set clickDebounceTime(value) {
        this._clickDebounceTime = coerceNumberProperty(value);
    }
    onClick(event) {
        event.preventDefault();
        event.stopPropagation();
        this._internalClick$.next(event);
    }
    prepareClickEnabled() {
        this._isActionEnabled = true;
    }
    ngOnInit() {
        this._internalClick$
            .pipe(filter(() => this._isActionEnabled), tap(() => (this._isActionEnabled = false)), throttle(() => timer(this.clickDebounceTime)), takeUntil(this._destroy$))
            .subscribe((event) => this.clickPreventChange.emit(event));
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
        this._internalClick$.complete();
    }
}
MClickDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MClickDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
MClickDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.9", type: MClickDirective, isStandalone: true, selector: "[m-click]", inputs: { clickDebounceTime: "clickDebounceTime" }, outputs: { clickPreventChange: "m-click" }, host: { listeners: { "click": "onClick($event)", "keydown.enter": "onClick($event)", "keydown.space": "onClick($event)", "mouseup": "prepareClickEnabled()", "keyup.enter": "prepareClickEnabled()", "keyup.space": "prepareClickEnabled()" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MClickDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[m-click]',
                    standalone: true
                }]
        }], propDecorators: { clickDebounceTime: [{
                type: Input
            }], clickPreventChange: [{
                type: Output,
                args: ['m-click']
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }, {
                type: HostListener,
                args: ['keydown.enter', ['$event']]
            }, {
                type: HostListener,
                args: ['keydown.space', ['$event']]
            }], prepareClickEnabled: [{
                type: HostListener,
                args: ['mouseup']
            }, {
                type: HostListener,
                args: ['keyup.enter']
            }, {
                type: HostListener,
                args: ['keyup.space']
            }] } });

class MClickOutsideElementDirective {
    constructor(elementRef) {
        this.elementRef = elementRef;
        this.clickOutside = new EventEmitter();
    }
    onClick(target) {
        const clickedInside = this.elementRef.nativeElement.contains(target);
        if (!clickedInside) {
            this.clickOutside.emit(target);
        }
    }
}
MClickOutsideElementDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MClickOutsideElementDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
MClickOutsideElementDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.9", type: MClickOutsideElementDirective, isStandalone: true, selector: "[m-click-outside]", outputs: { clickOutside: "m-click-outside" }, host: { listeners: { "document:click": "onClick($event.target)" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MClickOutsideElementDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[m-click-outside]',
                    standalone: true
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { clickOutside: [{
                type: Output,
                args: ['m-click-outside']
            }], onClick: [{
                type: HostListener,
                args: ['document:click', ['$event.target']]
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { MArrayGeneratorPipe, MClickDirective, MClickOutsideElementDirective, MCommonPipesModule, MDisplayTooltipEllipsisDirective, MHighlightSearchPipe, calculateTextWidth, isValidOpenKeyCode, stringSeparator };
